import Vue from 'vue';

import { Response } from '../../../core/types/http';
import { Folders } from './types';

const FOLDERS_API_URL = 'api/assets/v1/folders';

export default {
  /**
   * Get root folders.
   *
   * @param beforeRequestCb - callback to execute before the request is sent
   * @param sorting - optional sort
   * @returns the api response or error
   */
  async getRootFolders(
    sorting: { sortKey: string; direction: string } | null = null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    const sortParam = sorting
      ? encodeURIComponent(JSON.stringify([{ [sorting.sortKey]: sorting.direction }]))
      : undefined;

    return Vue.http.get(`${FOLDERS_API_URL}`, {
      params: {
        sorting: sortParam,
      },
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Get sub folders.
   *
   * @param parentId - The parent folder identifier
   * @param sorting - optional sort
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getChildrenFolders(
    parentId: string,
    sorting: { sortKey: string; direction: string } | null = null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    const sortParam = sorting
      ? encodeURIComponent(JSON.stringify([{ [sorting.sortKey]: sorting.direction }]))
      : undefined;

    return Vue.http.get(`${FOLDERS_API_URL}/${parentId}/children`, {
      params: {
        sorting: sortParam,
      },
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets the details of the folder
   *
   * @param folderId - The folder identifier
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFolder(
    folderId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FOLDERS_API_URL}/${folderId}`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets the details of the folder
   *
   * @param folderAlias - The alias of the folder
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFolderByAlias(
    folderAlias: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FOLDERS_API_URL}/alias=${folderAlias}`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets the form of the folder
   *
   * @param folderId - the folder to get the form of
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFolderForm(
    folderId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FOLDERS_API_URL}/${folderId}/fileForm`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Gets all attributes for a folder
   *
   * @param folderId - the ID of the folder
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async getFolderAttributes(
    folderId: string,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<unknown> {
    return Vue.http.get(`${FOLDERS_API_URL}/${folderId}/filesattributes`, {
      before(request: unknown) {
        if (beforeRequestCb) {
          beforeRequestCb(request);
        }
      },
    }) as Response<unknown>;
  },

  /**
   * Creates a folder
   *
   * @param folder - the folder creation object
   * @param parentFolder - optional id of the parent in which to create the folder.
   * Will be created under the root folder if unspecified.
   * @param beforeRequestCb - callback to execute before the request is sent
   * @returns the api response or error
   */
  async createFolder(
    folder: Folders.CreateFolderDto,
    parentFolder: string | null,
    beforeRequestCb: ((request: unknown) => void) | null = null
  ): Response<Folders.Folder> {
    return Vue.http.post(
      parentFolder ? `${FOLDERS_API_URL}/${parentFolder}` : FOLDERS_API_URL,
      folder,
      {
        before(request: unknown) {
          if (beforeRequestCb) {
            beforeRequestCb(request);
          }
        },
      }
    ) as Response<Folders.Folder>;
  },
};
